import S from 'common/service/sanctuary';
import * as R from 'ramda';
import React from 'react';
import { bool, element, string, arrayOf, node, func, oneOfType, symbol } from 'prop-types';
import classnames from 'classnames';
import { defineMessages, FormattedMessage } from 'react-intl';

// eslint-disable-next-line import/no-extraneous-dependencies
import { MDCSelectFoundation } from '@material/select/dist/mdc.select';
// eslint-disable-next-line import/no-extraneous-dependencies
import { MDCMenuFoundation } from '@material/menu/dist/mdc.menu';

import Menu from '../menu';

const messages = defineMessages(({
  placeholder: {
    id: 'mdc.select.placeholer',
    defaultMessage: 'Select ...',
  },
}));

class Select extends React.PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      classes: [],
    };

    this.close = this.close.bind(this);
    this.onChange = this.onChange.bind(this);
    this.handlers = {};

    this.foundation = new MDCSelectFoundation({
      addClass: (className) => {
        this.setState(({ classes }) => ({
          classes: R.append(className, classes),
        }));
      },
      removeClass: (className) => {
        this.setState(({ classes }) => ({
          classes: R.reject(R.equals(className), classes),
        }));
      },
      addClassToLabel: className => this.label && this.label.classList.add(className),
      removeClassFromLabel: className => this.label && this.label.classList.remove(className),
      addClassToBottomLine: className =>
        this.bottom.classList.add(className),
      removeClassFromBottomLine: className =>
        this.bottom && this.bottom.classList.remove(className),
      setBottomLineAttr: (attr, value) => this.bottom.setAttribute(attr, value),
      addBodyClass: className => document.body.classList.add(className),
      removeBodyClass: className => document.body.classList.remove(className),
      setAttr: (attr, value) => this.root.setAttribute(attr, value),
      rmAttr: (attr, value) => this.root.removeAttribute(attr, value),
      computeBoundingRect: () => this.surface.getBoundingClientRect(),
      registerInteractionHandler: (evtType, handler) => {
        this.surface.addEventListener(evtType, handler);
      },
      deregisterInteractionHandler: (evtType, handler) => {
        this.surface.removeEventListener(evtType, handler);
      },
      focus: () => this.surface && this.surface.focus(),
      makeTabbable: () => {
        this.surface.tabIndex = 0;
      },
      makeUntabbable: () => {
        this.surface.tabIndex = -1;
      },
      getComputedStyleValue: property => window
        .getComputedStyle(this.surface)
        .getPropertyValue(property),
      setStyle: (propertyName, value) => this.surface.style.setProperty(propertyName, value),
      create2dRenderingContext: () => document.createElement('canvas').getContext('2d'),
      setMenuElStyle: (propertyName, value) => {
        this.menu.style.setProperty(propertyName, value);
      },
      setMenuElAttr: (attr, value) => {
        this.menu.setAttribute(attr, value);
      },
      rmMenuElAttr: (attr) => {
        this.menu.removeAttribute(attr);
      },
      getMenuElOffsetHeight: () => this.menu.offsetHeight,
      openMenu: focusIndex => this.menuAdapter.open({ focusIndex }),
      isMenuOpen: () => this.menuAdapter.isOpen(),
      getNumberOfOptions: () => this.getDOMItems().length,
      getTextForOptionAtIndex: idx => S.maybe('', S.prop('innerText'), this.getDOMItem(idx)),
      getValueForOptionAtIndex: idx => this.props.children[idx].props.value,
      setAttrForOptionAtIndex: (idx, attr, value) => S.maybeToNullable(S.map(
        item => item.setAttribute(attr, value),
        this.getDOMItem(idx),
      )),
      rmAttrForOptionAtIndex: (idx, attr) => S.maybeToNullable(S.map(
        item => item.removeAttribute(attr),
        this.getDOMItem(idx),
      )),
      getOffsetTopForOptionAtIndex: idx => S.maybe(
        0,
        S.prop('offsetTop'),
        this.getDOMItem(idx),
      ),
      registerMenuInteractionHandler: (evtType, handler) => {
        this.handlers[evtType] = handler;
      },
      deregisterMenuInteractionHandler: (evtType) => {
        delete this.handlers[evtType];
      },
      notifyChange: () => {
        this.props.onChange(this.foundation.getValue());
      },
      getWindowInnerHeight: () => window.innerHeight,
    });
  }

  componentDidMount() {
    this.foundation.init();
    // this.foundation.setSelectedIndex();
  }

  componentWillUnmount() {
    this.foundation.destroy();
  }

  onChange({ index }) {
    this.handlers[MDCMenuFoundation.strings.SELECTED_EVENT]({
      detail: {
        index,
      },
    });
  }

  close() {
    this.handlers[MDCMenuFoundation.strings.CANCEL_EVENT]();
  }

  render() {
    const {
      dark,
      children,
      className,
      value,
      placeholder,
      label,
      ...props
    } = this.props;

    const { classes } = this.state;
    const selectedIdx = R.findIndex(
      selectItem => selectItem.props.value === value,
      this.props.children,
    );
    const selected = this.props.children[selectedIdx];
    const TextValue = selected && selected.props.children;

    return (
      <div
        ref={(elm) => { this.root = elm; }}
        className={classnames(
          'mdc-select',
          'mdc-select--box',
          { 'mdc-select--theme-dark': dark },
          ...classes,
          className,
        )}
        role="listbox"
      >
        <div
          tabIndex="0"
          className="mdc-select__surface"
          ref={(surface) => { this.surface = surface; }}
        >
          {label && (
            <div
              className="mdc-select__label"
              ref={(labelElement) => { this.label = labelElement; }}
            >{label}
            </div>
          )}
          <div className="mdc-select__selected-text">{ TextValue || placeholder }</div>
          <div
            className="mdc-select__bottom-line"
            ref={(bottom) => { this.bottom = bottom; }}
          />
        </div>
        <Menu
          {...props}
          className="mdc-select__menu"
          ref={(menu) => {
            this.menu = menu && menu.root;
            this.menuAdapter = menu && menu.foundation;
            this.getDOMItems = menu && menu.getDOMItems;
            this.getDOMItem = menu && menu.getDOMItem;
          }}
          onCancel={this.close}
          onSelect={this.onChange}
        >
          {React.Children.map(
            children,
            (item, idx) => React.cloneElement(item, {
              selected: idx === selectedIdx,
            }),
          )}
        </Menu>
      </div>
    );
  }
}

Select.propTypes = {
  value: oneOfType([
    node,
    symbol,
  ]),
  children: arrayOf(element).isRequired,
  className: string,
  placeholder: element,
  onChange: func,
  dark: bool,
};

Select.defaultProps = {
  dark: false,
  value: undefined,
  className: null,
  placeholder: <FormattedMessage {...messages.placeholder} />,
  onChange: () => undefined,
};

export default Select;
